import { create } from "zustand";
import { usersApi, organizationsApi } from "@services/api";
import type { User, Org, OrgWhatsApp } from "types";
import { DEFAULT_RECEPTIONS } from "types/org";
import { getFlutterFromDate } from "@services/timeHelpers";
import { useProjectStore } from "@store/projectStore";
import { useAuthStore } from "@store/auth";
import { setProxyBase } from "@services/serviceboxProxy";

/** IDs of categories that were formerly hardcoded as STATIC_CATEGORIES. */
const LEGACY_CATEGORY_IDS = ["rdv_divers", "location_rac", "vehicule_pret", "carrosserie"];

interface DataStore {
  userData: User | null;
  orgData: Org | null;
  orgMembers: User[];
  isLoadingUserData: boolean;
  isLoadingOrgData: boolean;
  isLoadingOrgMembers: boolean;
  userDataError: string | null;
  orgDataError: string | null;
  orgMembersError: string | null;

  // Actions
  fetchUserData: (userId: string) => Promise<void>;
  fetchOrgData: (orgId: string) => Promise<void>;
  fetchOrgMembers: (orgId: string) => Promise<void>;
  updateUserData: (userId: string, updates: any) => Promise<User>;
  updateOrgData: (orgId: string, updates: any) => Promise<Org>;
  clearUserData: () => void;
  clearOrgData: () => void;
  clearOrgMembers: () => void;
  clearAllData: () => void;
}

export const useDataStore = create<DataStore>((set, get) => ({
  userData: null,
  orgData: null,
  orgMembers: [],
  isLoadingUserData: false,
  isLoadingOrgData: false,
  isLoadingOrgMembers: false,
  userDataError: null,
  orgDataError: null,
  orgMembersError: null,

  fetchUserData: async (userId: string) => {
    set({ isLoadingUserData: true, userDataError: null });

    const MAX_RETRIES = 3;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) await new Promise((r) => setTimeout(r, 1500 * attempt));
        const userData = await usersApi.me(userId);

        set({
          userData,
          isLoadingUserData: false,
          userDataError: null,
        });

        // Auto-fetch org data, members, and projects if user has org_id
        if (userData.org_id) {
          await get().fetchOrgData(userData.org_id);
          await get().fetchOrgMembers(userData.org_id);
        }
        return;
      } catch (error: any) {
        console.error(`Error fetching user data (attempt ${attempt + 1}/${MAX_RETRIES}):`, error);
        if (attempt === MAX_RETRIES - 1) {
          set({
            userData: null,
            isLoadingUserData: false,
            userDataError: "Échec du chargement des données utilisateur",
          });
          await new Promise((r) => setTimeout(r, 2000));
          await useAuthStore.getState().logout();
        }
      }
    }
  },

  fetchOrgData: async (orgId: string) => {
    set({ isLoadingOrgData: true, orgDataError: null });
    console.log("📡 Fetching org data from API for org_id:", orgId);

    try {
      let orgDataResponse = await organizationsApi.get(orgId);
      console.log("✅ Org data fetched:", orgDataResponse);
      // Compute whatsapp.connected locally (backend @computed_field may not reach Firestore)
      const waRaw = orgDataResponse.whatsapp as OrgWhatsApp | undefined;
      if (waRaw) {
        const prov = waRaw.provider || "wasender";
        waRaw.connected = prov === "wasender"
          ? !!(waRaw.phone_number || waRaw.wasender_api_key)
          : !!waRaw.twilio_phone_number;
      }

      let orgData: Org = {
        ...orgDataResponse,
        creation_date: getFlutterFromDate(
          new Date(orgDataResponse.creation_date),
        ),
      };

      // Configure ServiceBox proxy URL from org settings
      if (orgData.servicebox?.proxy_url) {
        setProxyBase(orgData.servicebox.proxy_url);
      }

      // One-time migration: merge formerly hardcoded categories + set booking_type
      if (orgData.receptions) {
        let changed = false;
        let recs = [...orgData.receptions];
        const existingIds = new Set(recs.map((r) => r.id));
        const missing = DEFAULT_RECEPTIONS.filter(
          (d) => LEGACY_CATEGORY_IDS.includes(d.id) && !existingIds.has(d.id),
        );
        if (missing.length > 0) {
          recs = [...missing, ...recs];
          changed = true;
        }
        recs = recs.map((r) => {
          if (r.booking_type) return r;
          if (r.id === "carrosserie") { changed = true; return { ...r, booking_type: "carrosserie" as const }; }
          if (r.id.startsWith("reception_")) { changed = true; return { ...r, booking_type: "reception" as const }; }
          return r;
        });
        if (changed) {
          orgData = { ...orgData, receptions: recs };
          organizationsApi.update(orgId, { receptions: recs }).catch(() => {});
        }
      }

      set({
        orgData,
        isLoadingOrgData: false,
        orgDataError: null,
      });
    } catch (error: any) {
      console.error("❌ Error fetching org data:", error);
      set({
        orgData: null,
        isLoadingOrgData: false,
        orgDataError: "Échec du chargement des données de l'organisation",
      });
    }
  },

  fetchOrgMembers: async (orgId: string) => {
    set({ isLoadingOrgMembers: true, orgMembersError: null });
    console.log("📡 Fetching org members from API for org_id:", orgId);

    try {
      const orgMembers = await usersApi.list({ org_id: orgId });
      console.log("✅ Org members fetched:", orgMembers);

      set({
        orgMembers,
        isLoadingOrgMembers: false,
        orgMembersError: null,
      });
    } catch (error: any) {
      console.error("❌ Error fetching org members:", error);
      set({
        orgMembers: [],
        isLoadingOrgMembers: false,
        orgMembersError: "Échec du chargement des membres de l'équipe",
      });
    }
  },

  updateUserData: async (userId: string, updates: any) => {
    console.log("📡 Updating user data for userId:", userId);

    try {
      const updatedUser = await usersApi.update(userId, updates);
      console.log("✅ User data updated:", updatedUser);

      // Update local state if updating current user
      const currentUserData = get().userData;
      if (currentUserData?.id === userId) {
        set({ userData: updatedUser });
      }

      // Update in orgMembers list if present
      const orgMembers = get().orgMembers;
      const memberIndex = orgMembers.findIndex((m) => m.id === userId);
      if (memberIndex !== -1) {
        const newOrgMembers = [...orgMembers];
        newOrgMembers[memberIndex] = updatedUser;
        set({ orgMembers: newOrgMembers });
      }

      return updatedUser;
    } catch (error: any) {
      console.error("❌ Error updating user data:", error);
      throw error;
    }
  },

  updateOrgData: async (orgId: string, updates: any) => {
    console.log("📡 Updating org data for orgId:", orgId);

    try {
      let updatedOrgResponse = await organizationsApi.update(orgId, updates);
      console.log("✅ Org data updated:", updatedOrgResponse);

      let updatedOrg: Org = {
        ...updatedOrgResponse,
        creation_date: getFlutterFromDate(
          new Date(updatedOrgResponse.creation_date),
        ),
      };

      // Update local state
      set({ orgData: updatedOrg });

      return updatedOrg;
    } catch (error: any) {
      console.error("❌ Error updating org data:", error);
      throw error;
    }
  },

  clearUserData: () => {
    set({
      userData: null,
      isLoadingUserData: false,
      userDataError: null,
    });
  },

  clearOrgData: () => {
    set({
      orgData: null,
      isLoadingOrgData: false,
      orgDataError: null,
    });
  },

  clearOrgMembers: () => {
    set({
      orgMembers: [],
      isLoadingOrgMembers: false,
      orgMembersError: null,
    });
  },

  clearAllData: () => {
    get().clearUserData();
    get().clearOrgData();
    get().clearOrgMembers();
  },
}));
